import { print } from 'graphql';
import { gql } from 'graphql-tag';
import type { MigrationStrategies, RxCollection, RxDocument, RxJsonSchema } from 'rxdb';

import { getDateModified, getValueFromLocalStorage } from '../../utilities/utilityFunctions';
import { defaultDoc } from '../share/defaultDoc';

export interface PmCheckListChildren {
  readonly id: string;
  readonly assetCategoryInspectionTypeCheckListId: number;
  readonly assetCategoryId: number;
  readonly inspectionTypeName: string;
  readonly inspectionTypeId: number;
  readonly stepNo: number;
  readonly stepName: string;
  readonly stepDetails: string;
  readonly parentChecklist: number;
  readonly dateModified: string;
  readonly dateCreated: string;
  readonly site: number;
  readonly discipline: number;
  readonly isActive: boolean;
}

export interface PmCheckList extends PmCheckListChildren {
  readonly children: [PmCheckListChildren];
}

export const pmCheckListSchema: RxJsonSchema<PmCheckList> = {
  type: 'object',
  version: 1,
  description: 'describes pm check list',
  primaryKey: 'id',
  properties: {
    id: { type: 'string', maxLength: 36 },
    assetCategoryInspectionTypeCheckListId: { type: ['number', 'null'] },
    assetCategoryId: { type: ['number', 'null'] },
    inspectionTypeName: { type: ['string', 'null'] },
    inspectionTypeId: { type: ['number', 'null'] },
    stepNo: { type: ['number', 'null'] },
    stepName: { type: ['string', 'null'] },
    stepDetails: { type: ['string', 'null'] },
    parentChecklist: { type: ['number', 'null'] },
    dateCreated: { type: ['string', 'null'] },
    dateModified: { type: ['string', 'null'] },
    children: {
      type: ['array', 'null'],
      items: {
        type: ['object', 'null'],
        properties: {
          id: { type: 'string' },
          assetCategoryInspectionTypeCheckListId: { type: ['number', 'null'] },
          assetCategoryId: { type: ['number', 'null'] },
          inspectionTypeName: { type: ['string', 'null'] },
          inspectionTypeId: { type: ['number', 'null'] },
          stepNo: { type: ['number', 'null'] },
          stepName: { type: ['string', 'null'] },
          stepDetails: { type: ['string', 'null'] },
          parentChecklist: { type: ['number', 'null'] },
          dateCreated: { type: ['string', 'null'] },
          dateModified: { type: ['string', 'null'] },
          site: { type: ['number', 'null'] },
          discipline: { type: ['number', 'null'] },
          isActive: { type: ['boolean', 'null'] },
        },
      },
    },
    site: { type: ['number', 'null'] },
    discipline: { type: ['number', 'null'] },
    isActive: { type: ['boolean', 'null'] },
  },
};

export const pmCheckListMigrationStrategies: MigrationStrategies = {
  1: (oldDoc: PmCheckList) => {
    const newDoc = {
      ...oldDoc,
      site: 0,
      discipline: 0,
      isActive: false,
      children:
        oldDoc?.children?.length > 0
          ? oldDoc?.children?.map(child => ({ ...child, site: 0, discipline: 0, isActive: false }))
          : [],
    };
    return newDoc;
  },
};

export type PmCheckListCollection = RxCollection<PmCheckList> | null;
export type PmCheckListDocument = RxDocument<PmCheckList>;

export const pmCheckListPullQueryBuilder = (document: PmCheckList) => {
  let doc = document;
  if (!doc) {
    (doc as unknown) = { ...defaultDoc };
  }
  const query = print(gql`
    query mobilePmAssetInspectionChecklistInfo(
      $siteId: Int!
      $disciplineId: Int!
      $lastRequested: DateTime!
    ) {
      mobilePmAssetInspectionChecklistInfo(
        siteId: $siteId
        disciplineId: $disciplineId
        lastRequested: $lastRequested
      ) {
        assetCategoryInspectionTypeCheckListId
        parentChecklist
        id
        assetCategoryId
        stepNo
        stepName
        stepDetails
        inspectionTypeId
        inspectionTypeName
        dateModified
        dateCreated
        site
        discipline
        isActive
        children {
          assetCategoryInspectionTypeCheckListId
          parentChecklist
          id
          assetCategoryId
          stepNo
          stepName
          stepDetails
          inspectionTypeId
          inspectionTypeName
          dateModified
          dateCreated
          site
          discipline
          isActive
        }
      }
    }
  `);
  return {
    query,
    variables: {
      siteId: JSON.parse(getValueFromLocalStorage('selectedSite'))?.selectedSiteId,
      disciplineId: JSON.parse(getValueFromLocalStorage('selectedDiscipline'))
        ?.selectedDisciplineId,
      lastRequested: getDateModified(
        doc?.dateModified,
        doc?.dateCreated,
        doc.site,
        doc.discipline,
        JSON.parse(getValueFromLocalStorage('selectedSite'))?.selectedSiteId,
        JSON.parse(getValueFromLocalStorage('selectedDiscipline'))?.selectedDisciplineId,
      ),
    },
  };
};

export default {};
